import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

/**
 * 分析ページ・モバイル用 Vaul ボトムシートのスナップ計測と ref。
 * スナップ: 横棒のみ / トグル+日付 / コンテンツ展開
 */
export function useAnalysisMobileBottomSheet(isSmUp: boolean) {
    const [mobileMinHeightPx, setMobileMinHeightPx] = useState('48px');
    const [mobilePeekHeightPx, setMobilePeekHeightPx] = useState('280px');
    const [mobileExpandedHeightPx, setMobileExpandedHeightPx] = useState('480px');
    const [mobileAnalysisSnapPoint, setMobileAnalysisSnapPoint] = useState<number | string | null>('280px');

    const peekMeasureRef = useRef<HTMLDivElement>(null);
    const peekHandleOnlyRef = useRef<HTMLDivElement>(null);
    const mobileSheetScrollRef = useRef<HTMLDivElement>(null);
    const mobileExpandedInnerMeasureRef = useRef<HTMLDivElement>(null);
    const expandedSnapPxRef = useRef('480px');
    const minSnapPxRef = useRef('48px');

    const [mobileAnalysisSheetMeasureEl, setMobileAnalysisSheetMeasureEl] = useState<HTMLDivElement | null>(
        null,
    );
    const [mobileAnalysisSheetMeasureReady, setMobileAnalysisSheetMeasureReady] = useState(false);
    const [mobileAnalysisDrawerLayoutKey, setMobileAnalysisDrawerLayoutKey] = useState(0);
    const mobileAnalysisSheetMeasureHeightRef = useRef<number | null>(null);

    useLayoutEffect(() => {
        const el = mobileAnalysisSheetMeasureEl;
        mobileAnalysisSheetMeasureHeightRef.current = null;
        if (!el) {
            setMobileAnalysisSheetMeasureReady(false);
            return;
        }
        const sync = () => {
            const h = Math.round(el.getBoundingClientRect().height);
            const prev = mobileAnalysisSheetMeasureHeightRef.current;
            const ok = h > 0;
            setMobileAnalysisSheetMeasureReady(ok);
            if (!ok) return;
            if (prev !== null && prev !== h) {
                setMobileAnalysisDrawerLayoutKey((k) => k + 1);
            }
            mobileAnalysisSheetMeasureHeightRef.current = h;
        };
        sync();
        let ro: ResizeObserver | undefined;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(sync);
            ro.observe(el);
        }
        window.addEventListener('resize', sync);
        return () => {
            ro?.disconnect();
            window.removeEventListener('resize', sync);
        };
    }, [mobileAnalysisSheetMeasureEl]);

    useLayoutEffect(() => {
        if (!mobileAnalysisSheetMeasureReady) return;
        const head = peekMeasureRef.current;
        const scrollEl = mobileSheetScrollRef.current;
        const outer = mobileAnalysisSheetMeasureEl;
        if (!head || !outer) return;

        const sync = () => {
            const ch = Math.round(outer.getBoundingClientRect().height);
            const headH = Math.ceil(head.scrollHeight);
            const scrollH = scrollEl ? scrollEl.scrollHeight : 0;
            const peekNum = Number.parseInt(mobilePeekHeightPx, 10);
            if (ch <= 0 || headH <= 0 || !Number.isFinite(peekNum)) return;
            const raw = headH + scrollH + 8;
            const capped = Math.min(raw, Math.floor(ch * 0.96));
            const next = Math.max(capped, peekNum + 40);
            const px = `${next}px`;
            const prevExp = expandedSnapPxRef.current;
            expandedSnapPxRef.current = px;
            setMobileExpandedHeightPx(px);
            setMobileAnalysisSnapPoint((cur) =>
                cur === prevExp || cur === 1 || cur === '1' ? px : cur,
            );
        };

        sync();
        let ro: ResizeObserver | undefined;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(sync);
            ro.observe(head);
            ro.observe(outer);
            const inner = mobileExpandedInnerMeasureRef.current;
            if (inner) ro.observe(inner);
            if (scrollEl) ro.observe(scrollEl);
        }
        window.addEventListener('resize', sync);
        return () => {
            ro?.disconnect();
            window.removeEventListener('resize', sync);
        };
    }, [
        mobileAnalysisSheetMeasureReady,
        mobileAnalysisSheetMeasureEl,
        mobileAnalysisDrawerLayoutKey,
        mobilePeekHeightPx,
    ]);

    useLayoutEffect(() => {
        if (!mobileAnalysisSheetMeasureReady) return;
        const head = peekMeasureRef.current;
        const container = mobileAnalysisSheetMeasureEl;
        if (!head || !container) return;

        const sync = () => {
            const headH = Math.ceil(head.scrollHeight);
            const ch = Math.round(container.getBoundingClientRect().height);
            if (headH <= 0 || ch <= 0) return;
            const target = Math.min(headH + 12, Math.floor(ch * 0.95));
            const px = `${target}px`;
            setMobilePeekHeightPx(px);
            setMobileAnalysisSnapPoint((prev) => {
                if (prev === 1) return expandedSnapPxRef.current;
                if (prev === expandedSnapPxRef.current) return prev;
                if (prev === minSnapPxRef.current) return prev;
                return px;
            });
        };

        sync();
        let ro: ResizeObserver | undefined;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(sync);
            ro.observe(head);
            ro.observe(container);
        }
        window.addEventListener('resize', sync);
        return () => {
            ro?.disconnect();
            window.removeEventListener('resize', sync);
        };
    }, [mobileAnalysisSheetMeasureReady, mobileAnalysisSheetMeasureEl, mobileAnalysisDrawerLayoutKey]);

    useLayoutEffect(() => {
        if (!mobileAnalysisSheetMeasureReady) return;
        const handleEl = peekHandleOnlyRef.current;
        const container = mobileAnalysisSheetMeasureEl;
        if (!handleEl || !container) return;

        const sync = () => {
            const ch = Math.round(container.getBoundingClientRect().height);
            const h = Math.ceil(Math.max(handleEl.scrollHeight, handleEl.getBoundingClientRect().height));
            const peekNum = Number.parseInt(mobilePeekHeightPx, 10);
            if (ch <= 0 || h <= 0 || !Number.isFinite(peekNum)) return;
            let target = h + 8;
            target = Math.min(target, peekNum - 28);
            target = Math.max(target, 28);
            const px = `${target}px`;
            const prevMin = minSnapPxRef.current;
            minSnapPxRef.current = px;
            setMobileMinHeightPx(px);
            setMobileAnalysisSnapPoint((cur) => (cur === prevMin ? px : cur));
        };

        sync();
        let ro: ResizeObserver | undefined;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(sync);
            ro.observe(handleEl);
            ro.observe(container);
        }
        window.addEventListener('resize', sync);
        return () => {
            ro?.disconnect();
            window.removeEventListener('resize', sync);
        };
    }, [
        mobileAnalysisSheetMeasureReady,
        mobileAnalysisSheetMeasureEl,
        mobileAnalysisDrawerLayoutKey,
        mobilePeekHeightPx,
    ]);

    useEffect(() => {
        if (isSmUp) setMobileAnalysisSnapPoint(mobilePeekHeightPx);
    }, [isSmUp, mobilePeekHeightPx]);

    const isMobileAnalysisSheetExpanded = useMemo(
        () => mobileAnalysisSnapPoint === mobileExpandedHeightPx,
        [mobileAnalysisSnapPoint, mobileExpandedHeightPx],
    );

    return {
        setMobileAnalysisSheetMeasureEl,
        mobileAnalysisSheetMeasureEl,
        mobileAnalysisSheetMeasureReady,
        mobileAnalysisDrawerLayoutKey,
        mobileMinHeightPx,
        mobilePeekHeightPx,
        mobileExpandedHeightPx,
        mobileAnalysisSnapPoint,
        setMobileAnalysisSnapPoint,
        peekMeasureRef,
        peekHandleOnlyRef,
        mobileSheetScrollRef,
        mobileExpandedInnerMeasureRef,
        isMobileAnalysisSheetExpanded,
    };
}
